// @ts-nocheck

import {
  add,
  findIndex,
  get,
  groupBy,
  isEmpty,
  isNil,
  round,
  sortBy,
  subtract,
  sumBy,
} from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';
import { RoleCode } from 'src/components/Exchange/types';
import config from 'src/config/config';
import {
  AGREEMENT_TYPES,
  BANK_STATUS,
  BUY_TRANSACTION,
  COLLEGE_DISTRIBUTE_MAX_REDEMPTION,
  COLLEGE_DISTRIBUTE_PAYEE_OPTIONS,
  DELIVERY_MAIL_CHECK,
  DELIVERY_METHODS,
  EARLY_WITHDRAWAL_PENALTY_AGE,
  EXCHANGE_TRANSACTION,
  MAXIMUM_BY_DELIVERY_METHOD,
  MD529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION,
  MIN_REDEEMABLE_THRESHOLD_DOLLAR_PERCENTAGE,
  NON_RETIREMENT,
  SELECTED_UNIT_ENUM_OBJECT_MAP,
  SELL_TRANSACTION,
  SystemCode,
  UNIT,
  DELIVERY_IN_KIND,
  ProductTypes,
  AK529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION,
  NAT529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION,
  FederalTaxPercent,
} from 'src/settings/settings';
import { ItemSelection, SystematicSellFund, UserFund } from 'src/types';
import { getLastFour } from 'src/utils/banking/bank-utils';
import {
  formatUTCDate,
  getCurrentDate,
  getCurrentYear,
  getMonthName,
  previousYear,
} from 'src/utils/dates/date-utility';
import { validateSellFund } from 'src/utils/form/validate/validate';
import {
  agreementKey,
  calculateSellTransactionFundDetails,
  firstAvailableUncollectedSharesUnixDate,
  getHasUncollectedShares,
  getHasUnavailableDelivery,
  isSelectedWithAmount,
  setCollegeExchangeSharePrice,
  setFundPendingTransaction,
  setMoneyMarketReformFundMessages,
  setSellFundLimitMessage,
  setWireFee,
} from 'src/utils/funds/funds-utility';
import { updateSellFundNotesForFund } from 'src/utils/funds/sell/sell-fund-notes-utility';
import { checkFoundFeaturesByCode } from 'src/utils/funds/shared/shared-fund-notes-utility';
import { appendBasePath, PRODUCT_TYPE } from 'src/utils/nav.utils';
import {
  isAutomaticSell,
  isCollegeDistribute,
} from 'src/utils/transactions-util';
import { coalesce, currency, hasValue, sumArray } from 'src/utils/utils';
import { updateAutomaticSellFundNotesForFund } from '../../../utils/funds/autoSell/auto-sell-fund-notes-utility';
import { automaticEditActivityID } from '../automaticEdit/automaticEdit.selectors';
import {
  getAutoExchanges,
  getAutoMax,
  getRecurringPurchases,
  getRecurringRedemptions,
} from '../automaticInvestments/automaticInvestments.selectors';
import { SystematicExchange } from '../automaticInvestments/types';
import {
  getSelectedBank,
  getSelectedBankAgreementMap,
  getSelectedBankAssociableLookup,
} from '../bankSelection/bankSelection.selectors';
import { getCostBasisMap } from '../costBasis/costBasis.selectors';
import { CostBasisTransactionState } from '../costBasis/types';
import { getEligibleBanks } from '../existingBanks/existingBanks.selectors';
import { getCollegeAllFundsMap } from '../funds/AllFunds/allFunds.selectors';
import { getMoneyMarketReformFundMessages } from '../moneyMarketEvent/moneyMarketEvent.selectors';
import {
  getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
  getSellPendingTransactions,
} from '../pendingTransactions/pendingTransactions.selectors';
import { PendingTransaction } from '../pendingTransactions/types';
import {
  getHasAPP,
  getHasCollegeOnly,
  getHasMD529Only,
  getHasAK529Only,
  getHasNAT529Only,
} from '../productTypes/productType.selectors';
import { getRedeemThresholds } from '../redeemThresholds/redeemThresholds.selectors';
import {
  getIsRMDDisclaimerAccepted,
  getIsRMDEligible,
  getRMDAvailableBalance,
  getSystematicLE,
  getTotalPendingRMDAmount,
  getTotalSystematicRMDAmount,
  getYearUserTurningRMDEligible,
} from '../RMD/RMDSetting.selectors';
import {
  getDeliveryMethod,
  getSelectedDeliveryMethod,
} from '../sellDeliveryMethod/sellDeliveryMethod.selectors';
import { getUncollectedSharesLookup } from '../sellUncollectedShares/selectors';
import { SellUncollectedSharesState } from '../sellUncollectedShares/types';
import {
  getIsNonRetirementLikeFund,
  getRecurringRedemptionFunds,
  getUserFundList,
  getAutoExchangeFunds,
} from '../shoppingCart/shoppingCart.selectors';
import { getUserStateWithholdingRules } from '../stateWithholding/selectors';
import { getTransactionLimits } from '../transactionLimits/transactionLimits.selectors';
import { TransactionLimit } from '../transactionLimits/types';
import {
  getCustomerSegmentName,
  getHasServiceAccountFee,
  getPersonalInformationData,
  getUserAddress,
  isDateOfBirthAvailable,
} from '../user/PersonalInformation/personalInformation.selectors';
import { PersonalInformationState } from '../user/PersonalInformation/types';
import { SellFund, SellLookup } from './types';
import { isCustomerWebViewEnabled } from '../../../redux/modules/authentication/authentication.module.selectors';
import { isSalesforceRep } from '../../../redux/modules/authentication/authentication.module.selectors';
import {
  getSystematicActivityRMDs,
  getRMDAgreements,
} from '../RMD/RMDSetting.selectors';
import { getSelectedSchool } from '../schoolSelection';
import { getStudentDetails, hasStudentID } from '../studentDetail';

import { isRAS } from '../../../utils/advice/advice-utility';
import { isIRA } from '../../../utils/funds/typed-funds-utility';
/**
 * @typedef {object} SellTransactionItem individual item in the sell transaction.
 * @property {boolean} selected If item is currently selected. Non-selected items can be in the transaction to preserve
 * previously entered amounts in case they are re-selected.
 * @property {number} grossAmount
 * @property {string} grossUnit
 *
 * WebStorm isn't mixing in SellTransactionItem to SellFund, so above props duplicated
 * in SellFund below.
 */

/**
 * @typedef {Object.<string, SellTransactionItem>} SellTransactionLookup Sell transaction lookup object, key is a fund agreementID
 */

/**
 * Sell fund sorter sorts non-retirement first, retirement second, and then by same sort order that buy uses
 * @param {UserFund} x
 * @param {UserFund} y
 * @returns {number} -1, 0, 1
 */
function sellFundSorter(x, y) {
  const { displayClassification: xDisplayClassification, sortRank: xSortRank } =
    x;
  const { displayClassification: yDisplayClassification, sortRank: ySortRank } =
    y;

  return xDisplayClassification === yDisplayClassification
    ? xSortRank - ySortRank
    : xDisplayClassification === 'NON_RETIREMENT'
    ? -1
    : 1;
}

/**
 * Sell fund filter that removes zero balance funds without uncollected shares
 * @param {UserFund} fund
 * @returns array of funds without those with zero balances
 */
function sellFundFilter(fund) {
  const { balanceAsOf, equityAsOf } = fund;

  return (
    (balanceAsOf > 0 && equityAsOf > 0) || (equityAsOf === 0 && balanceAsOf > 0)
  );
}

/**
 * Simple selector to extract cartLookup from shopping cart.
 * @param state
 * @returns {SellTransactionLookup}
 */
export const getSellTransactionLookup = (state) =>
  state.sellTransaction.sellLookup;

/**
 * @typedef {UserFund} SellFund
 *
 * WebStorm doesn't support at-mixes, so duplicating props here:
 *
 * @mixes SellTransactionItem
 * @property {boolean} selected If item is currently selected. Non-selected items can be in the transaction to preserve
 * previously entered amounts in case they are re-selected.
 * @property {number} grossAmount
 * @property {string} grossUnit
 *
 * From server, move to UserFund...:
 *
 * @property {number} equityAsOf Subset of total balance that is available to sell
 * @property {Array} chargeables Object with information on redemption fees due upon sale, if applicable
 * @property {number} chargeables[].shareQuantity Quantity of shares subject to redemption fee
 * @property {string} chargeables[].chargeableSharesReleaseDate Date that the chargeable shares are no longer chargeable
 * @property {number} shares Number of shares the user has
 * @property {number} sharePrice
 * @property {number} closeoutFee
 *
 * Calculated:
 *
 * @property {number} grossAmountDollars Gross amount converted to dollars
 * @property {number} grossPercentShares Gross amount in percentage converted to shares
 * @property {number} chargeableShareQuantity Copy of quantity of shares subject to redemption fee, for convenience
 * @property {boolean} [federalWithholdingApplied] Whether or not federal withholding is applied (checked)
 * @property {number} federalWithholdingPercent Percentage of gross to be withheld for federal
 * @property {number} federalWithholdingDollars Calculated federal withholding in dollars, based on percent
 * @property {boolean} stateWithholdingApplied Whether or not state withholding is applied (checked)
 * @property {number} stateWithholdingPercent Percentage used for state calculation (which can vary by state)
 * @property {number} stateWithholdingDollars Calculated state withholding in dollars, based on percent
 * @property {number} netDollarAmount
 * @property {boolean} netDollarIsEstimate
 * @property {boolean} stateIsEstimate
 * @property {number} uncollectedShares
 * @property {number} totalDeductions
 * @property {boolean} isTakeHomeEstimated
 * @property {boolean} hasRecurring

 *
 * Validation:
 *
 * @property {string} grossAmountError Error message if gross amount has a problem
 */

/**
 * Selector that filters and extracts redeem transaction limit (minimum and maximum) from
 * getTransactionLimits selector based on selected delivery method
 * @param state
 * @returns {object}
 */
export const getTransactionLimit = createSelector(
  [
    isSalesforceRep,
    isCustomerWebViewEnabled,
    getHasCollegeOnly,
    getHasMD529Only,
    getHasAK529Only,
    getHasNAT529Only,
    getDeliveryMethod,
    getTransactionLimits,
  ],
  (
    salesforceRep,
    customerWebViewEnabled,
    hasCollegeOnly,
    hasMd529Only,
    hasAk529Only,
    hasNat529Only,
    deliveryMethod,
    transactionLimits: TransactionLimit[]
  ) => {
    const transactionLimit = transactionLimits.filter((limit) => {
      const {
        criteria: {
          agreementTypeCategory,
          productType,
          fundingMethod,
          fundType,
        },
      } = limit;
      // Todo: Remove hard coded filter values after backend update.
      if (
        !isNil(productType) &&
        productType.enumerationName === PRODUCT_TYPE.MUTUAL_FUND &&
        !isNil(fundingMethod) &&
        fundingMethod.enumerationName === deliveryMethod &&
        !isNil(fundType) &&
        fundType.enumerationName === 'Regular' &&
        !isNil(agreementTypeCategory) &&
        agreementTypeCategory.enumerationName === 'Regular'
      ) {
        return limit;
      }
      return false;
    })[0];

    const limit = {} as TransactionLimit;
    limit.minimum = 1;

    if (!customerWebViewEnabled && salesforceRep) {
      if (
        deliveryMethod === DELIVERY_METHODS.WIRE ||
        deliveryMethod === DELIVERY_METHODS.MAIL_A_CHECK
      ) {
        limit.maximum = Infinity;
      }

      if (deliveryMethod === DELIVERY_METHODS.ACH) {
        limit.maximum = 250000;
      }

      return limit;
    }

    if (isCollegeDistribute()) {
      limit.minimum = deliveryMethod === DELIVERY_METHODS.WIRE ? 100 : 0;
      limit.maximum = hasMd529Only
        ? MD529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION
        : hasAk529Only
        ? AK529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION
        : hasNat529Only
        ? NAT529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION
        : COLLEGE_DISTRIBUTE_MAX_REDEMPTION;
      return limit;
    }

    if (isAutomaticSell()) {
      limit.minimum = 50;
      limit.maximum = 25000;
      return limit;
    }

    if (!isNil(transactionLimit)) {
      return transactionLimit;
    }

    switch (deliveryMethod) {
      case DELIVERY_METHODS.ACH:
        limit.maximum = coalesce(
          MAXIMUM_BY_DELIVERY_METHOD[DELIVERY_METHODS.ACH],
          0
        );
        break;
      case DELIVERY_METHODS.WIRE:
        limit.maximum = coalesce(
          MAXIMUM_BY_DELIVERY_METHOD[DELIVERY_METHODS.WIRE],
          0
        );
        break;
      case DELIVERY_METHODS.MAIL_A_CHECK:
        limit.maximum = coalesce(
          MAXIMUM_BY_DELIVERY_METHOD[DELIVERY_METHODS.MAIL_A_CHECK],
          0
        );
        break;
      default:
        limit.maximum = 0;
        break;
    }
    return limit;
  }
);

function addressKey(address) {
  if (address === null) {
    return '';
  }
  const newAddress =
    address.street1 +
    '-' +
    address.city +
    '-' +
    address.state +
    '-' +
    address.zip;
  return newAddress;
}

/**
 * Simple selector to extract the hasMultipleAddresses flag from information call
 * @param state
 * @returns {boolean}
 */
export const getMultipleAddressFlag = (state) =>
  state.userFunds.funds?.length &&
  Boolean(state.userFunds.funds[0].hasMultipleAddresses);

/**
 * Simple selector to extract the displayRMD flag from information call
 * @returns {boolean}
 */
export const getHasNotInheritedSEPOrSimpleIRA = (state) =>
  state.userFunds.funds[0] ? state.userFunds.funds[0].displayRMD : false;

/**
 * Complex selector to extract the estimateDates for ExpressDelivery.
 * @param method - Delivery Method
 * @returns {selector} - should return a boolean selector based on the delivery method passed compared to current
 * delivery selection
 */
function confirmDeliveryMethod(method) {
  return createSelector(
    getDeliveryMethod,
    (deliveryMethod) => deliveryMethod === method
  );
}

/**
 * Complex selector to extract if delivery method is of a certain type (mail a check)
 * @param state
 * @returns {boolean}
 */
export const getIsMailACheck = confirmDeliveryMethod(
  DELIVERY_METHODS.MAIL_A_CHECK
);

export const getIsMailACheckToSchool = confirmDeliveryMethod(
  DELIVERY_METHODS.MAIL_A_CHECK_SCHOOL
);

/**
 * Complex selector to extract if delivery method is of a certain type (wire)
 * @param state
 * @returns {boolean}
 */
export const getIsWire = confirmDeliveryMethod(DELIVERY_METHODS.WIRE);

/**
 * Complex selector to extract if delivery method is of a certain type (ach)
 * @param state
 * @returns {boolean}
 */
export const getIsACH = confirmDeliveryMethod(DELIVERY_METHODS.ACH);

/**
 * Complex selector to extract if delivery method is of MailACheck and has multiple address on file.
 * @param state
 * @returns {boolean}
 */
export const getIsCheckWithMultipleAddress = createSelector(
  getIsMailACheck,
  getIsMailACheckToSchool,
  getMultipleAddressFlag,
  (isMailACheck, isMailACheckToSchool, multipleAddressFlag) =>
    (isMailACheck || isMailACheckToSchool) && multipleAddressFlag
);

export function getCoveredShares(agreementKey) {
  return (
    agreementKey.costBasisBalance &&
    agreementKey.costBasisBalance.covered &&
    agreementKey.costBasisBalance.covered.totalShares
  );
}

export function getUncoveredShares(agreementKey) {
  return (
    agreementKey.costBasisBalance &&
    agreementKey.costBasisBalance.uncovered &&
    agreementKey.costBasisBalance.uncovered.totalShares
  );
}

export const getIsRMDEligiblePreviousYear = createSelector(
  getYearUserTurningRMDEligible,
  (yearUserTurningRMDEligible) => {
    const dateTillRMDTableHidden = `${getCurrentYear('YYYY')}-04-01`;
    return (
      yearUserTurningRMDEligible === previousYear(1, 'YYYY') &&
      moment().isBefore(dateTillRMDTableHidden)
    );
  }
);
export const getQuickSellAgreementID = (state) =>
  state.quickSell.quickSellAgreements[0];

export const getIsQuickSellWithoutZeroBalance = createSelector(
  [getUserFundList, getQuickSellAgreementID],
  (userFundList: UserFund[], quickSellAgreementID: string) => {
    const funds =
      !!quickSellAgreementID &&
      userFundList.filter((fund) => fund.agreementID === quickSellAgreementID);
    return !!funds?.[0] && sellFundFilter(funds[0]);
  }
);
export const getFilteredSellFundsList = createSelector(
  getUserFundList,
  (userFundList: UserFund[]) => {
    return userFundList.filter((fund) => sellFundFilter(fund));
  }
);
/**
 * Computed list of funds available to sell including sell-specific information and sell transaction
 * @param state
 * @returns {SellFund[]}
 * @ts-ignore
 */
export const getSellFundsList = createSelector(
  [
    getCollegeAllFundsMap,
    getSelectedBank,
    getCustomerSegmentName,
    getDeliveryMethod,
    getHasServiceAccountFee,
    getIsRMDEligible,
    getMoneyMarketReformFundMessages,
    getPersonalInformationData,
    getRedeemThresholds,
    getSelectedBankAgreementMap,
    getSellTransactionLookup,
    getUncollectedSharesLookup,
    getCostBasisMap,
    getFilteredSellFundsList,
    getUserStateWithholdingRules,
    getIsCheckWithMultipleAddress,
    getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    getAutoMax,
    getAutoExchanges,
    getRecurringPurchases,
    getRecurringRedemptions,
    isDateOfBirthAvailable,
    getSelectedBankAssociableLookup,
    getQuickSellAgreementID,
    getIsQuickSellWithoutZeroBalance,
    getSystematicActivityRMDs,
  ],
  (
    collegeAllFundsMap,
    selectedBank,
    customerSegmentName,
    deliveryMethod,
    hasServiceAccountFee,
    isRMDEligible,
    moneyMarketReformFundMessages,
    _personalInfo,
    redeemThresholds,
    selectedBankAgreementMap,
    _sellLookup,
    _uncollectedSharesLookup,
    _costBasisLookup,
    _filteredSellFundsList,
    userStateWithholdingRules,
    isCheckWithMultipleAddress,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    autoMax,
    _autoExchanges,
    _recurringPurchases,
    _recurringRedemptions,
    dateOfBirthAvailable,
    _selectedBankAssociableLookup,
    _quickSellAgreementID,
    _isQuickSellWithoutZeroBalance,
    autoRMDs
  ) => {
    /**
     * Typescript seems to be confused by the amount of params
     * Temporarily re-cast these vars to allow the type checker to complete
     */
    const filteredSellFundsList = _filteredSellFundsList as UserFund[];
    const isQuickSellWithoutZeroBalance =
      _isQuickSellWithoutZeroBalance as boolean;
    const quickSellAgreementID = _quickSellAgreementID as string;
    const sellLookup = _sellLookup as SellLookup;
    const uncollectedSharesLookup =
      _uncollectedSharesLookup as SellUncollectedSharesState['uncollectedShares'];
    const costBasisLookup =
      _costBasisLookup as CostBasisTransactionState['costBasisMap'];
    const personalInfo = _personalInfo as PersonalInformationState['data'];
    const selectedBankAssociableLookup =
      _selectedBankAssociableLookup as Record<string, string>;
    const autoExchanges = _autoExchanges as Record<
      string,
      SystematicExchange[]
    >;
    const recurringPurchases = _recurringPurchases as Record<string, boolean>;
    const recurringRedemptions = _recurringRedemptions as Record<
      string,
      boolean
    >;

    return (
      isQuickSellWithoutZeroBalance
        ? filteredSellFundsList.filter(
            (fund) => fund.agreementID === quickSellAgreementID
          )
        : filteredSellFundsList
    )
      .map((sourceFund) => {
        const {
          address,
          agreementID,
          agreementNumber,
          agreementType,
          balanceAsOf,
          chargeables,
          displayClassification,
          equityAsOf,
          productType,
          sweep,
          trpCode,
          uniqueKey,
        } = sourceFund;

        const applyCloseoutFee =
          !sweep &&
          displayClassification !== NON_RETIREMENT &&
          !hasServiceAccountFee;
        const chargeableShareQuantity = sumArray(
          chargeables,
          (c) => c.shareQuantity,
          0
        );
        const redeemableThresholdDollarPercentage = get(
          redeemThresholds,
          trpCode,
          MIN_REDEEMABLE_THRESHOLD_DOLLAR_PERCENTAGE
        );
        const sellTransactionItem = sellLookup[agreementID] || {};
        const uncollectedShares = uncollectedSharesLookup[agreementID] || {};
        const hasMultipleTransactionsUncollected =
          /** @ts-expect-error this is an object, but the existing code thinks it's an array */
          uncollectedShares?.length > 1;
        const hasUncollectedShares = getHasUncollectedShares(
          balanceAsOf,
          equityAsOf,
          productType
        );
        const hasUnavailableDelivery = getHasUnavailableDelivery(address);
        const settlementDate =
          firstAvailableUncollectedSharesUnixDate(uncollectedShares);
        const costBasisData =
          costBasisLookup[agreementKey(agreementID, agreementNumber)] || {};
        const nonRetirementLike = getIsNonRetirementLikeFund(sourceFund);
        const isFundRMDEligible =
          !!isRMDEligible &&
          (agreementType === AGREEMENT_TYPES.ROLLOVER_IRA ||
            agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA) &&
          !!dateOfBirthAvailable;
        const fundAssociableKey = selectedBankAssociableLookup[uniqueKey] || {};
        const recurringTransactionTypes: string[] = [];

        if (autoExchanges[agreementID]) {
          recurringTransactionTypes.push(EXCHANGE_TRANSACTION);
        }

        if (recurringPurchases[agreementID]) {
          recurringTransactionTypes.push(BUY_TRANSACTION);
        }

        if (recurringRedemptions[agreementID]) {
          recurringTransactionTypes.push(SELL_TRANSACTION);
        }
        /** @type SellFund */
        const fund: SellFund = {
          ...sourceFund,
          closeoutFee:
            equityAsOf > 20 && sellTransactionItem.closeout && applyCloseoutFee
              ? 20
              : 0,
          notes: {},
          disabled: false,
          bankAssociated: true,
          hasUncollectedShares,
          hasUnavailableDelivery,
          chargeableShareQuantity,
          nonRetirementLike,
          hasChargeableShareQuantity: (chargeableShareQuantity as number) > 0,
          isFundAssociable: fundAssociableKey && hasValue(fundAssociableKey),
          hasEarlyWithdrawalPenalty:
            !!personalInfo?.age &&
            personalInfo.age < EARLY_WITHDRAWAL_PENALTY_AGE &&
            !nonRetirementLike,
          hasMultipleTransactionsUncollected,
          hasUncollectedSharesDate:
            hasUncollectedShares && hasValue(settlementDate),
          isFundRMDEligible,
          uncollectedSharesQuantity: sumArray(
            uncollectedShares,
            (uncollectedShare) => uncollectedShare.shares,
            0
          ),
          redeemableThresholdDollarPercentage,
          redeemableFundDollars:
            isAutomaticSell() || isRAS(productType)
              ? balanceAsOf
              : redeemableThresholdDollarPercentage === 100
              ? equityAsOf
              : Math.floor(redeemableThresholdDollarPercentage * equityAsOf) /
                100,
          settlementDate,
          addressKey: addressKey(address),
          coveredShares: getCoveredShares(costBasisData),
          uncoveredShares: getUncoveredShares(costBasisData),
          hasRecurring:
            !!(autoMax as Record<string, boolean>)[agreementID] ||
            !!recurringPurchases[agreementID] ||
            !!recurringRedemptions[agreementID] ||
            !!autoExchanges[agreementID],
          ...sellTransactionItem,
          recurringTransactionTypes,
        };
        if (isCollegeDistribute()) {
          setCollegeExchangeSharePrice(fund, collegeAllFundsMap);
        }
        setMoneyMarketReformFundMessages(fund, moneyMarketReformFundMessages);
        setFundPendingTransaction(
          fund,
          pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
        );
        updateSellFundNotesForFund(
          deliveryMethod,
          fund,
          isCheckWithMultipleAddress,
          selectedBankAgreementMap,
          selectedBank
        );
        if (isAutomaticSell()) {
          updateAutomaticSellFundNotesForFund(fund, autoRMDs);
        }
        calculateSellTransactionFundDetails(fund, userStateWithholdingRules);
        setWireFee(customerSegmentName, deliveryMethod, fund);
        return fund;
      })
      .sort(sellFundSorter);
  }
);

/**
 * @param getSellTransactionLookup {Selector} - gets sellLookup Object that contains shopping cart selections
 * @param getUserFundList {Selector} - gets all user funds
 * @returns {Array} this returns a full crossover object of selected funds with all userFund attributes as well as
 * written data from the sell lookup
 */
export const getSelectedSellFundsList = createSelector(
  [getSellFundsList, getSelectedDeliveryMethod],
  (sellFundsList, deliveryMethod) =>
    sellFundsList.filter((f) => {
      return (
        f.selected &&
        (f.grossAmountDollars || deliveryMethod === DELIVERY_IN_KIND)
      );
    })
);

export const isSelectedFundRetirement = createSelector(
  [getSelectedSellFundsList],
  (sellFundsList) =>
    sellFundsList.some(
      (fund) =>
        fund.displayClassification === 'RETIREMENT' &&
        (fund?.agreementType === 'TRADITIONAL_IRA' ||
          fund?.agreementType === 'ROLLOVER_IRA')
    )
);

/**
 * Complex selector to check whether the user is RMD eligible or not for calling RMD-Summary service.
 * @param getUserFundList {Selector}
 * @param getIsRMDEligible {Selector}
 * @returns {Boolean}
 */
export const getIsUserWithAtleastOneRMDEligibleFund = createSelector(
  [getUserFundList, getIsRMDEligible],
  (userFundList: SellFund[], isRMDEligible) => {
    return (
      isRMDEligible &&
      userFundList.some(
        (fund) =>
          fund.agreementType === AGREEMENT_TYPES.ROLLOVER_IRA ||
          fund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA
      )
    );
  }
);

/**
 * Complex selector to calculate Remaining RMD Balance Which is shown in step2 of sell flow.
 * @param getSelectedSellFundsList {Selector}
 * @param getRMDAvailableBalance {Selector}
 * @returns {number} amount
 */
export const getRMDRemainingBalanceAmount = createSelector(
  [
    getSelectedSellFundsList,
    getRMDAvailableBalance,
    getTotalPendingRMDAmount,
    getTotalSystematicRMDAmount,
    getSystematicLE,
  ],
  (
    sellFundList,
    rmdAvailableBalance: number,
    totalPendingRMDAmount: number,
    totalSystematicRMDAmount: number,
    hasSystematicLE
  ) => {
    const funds = sellFundList.filter((f) => f.isFundRMDEligible);
    function remainingRMDBalance(amount, fund) {
      return amount - coalesce(fund.grossAmountDollars, 0);
    }
    const totalPendingAndSystematicRMDAmount = add(
      totalPendingRMDAmount,
      totalSystematicRMDAmount
    );
    const totalRemainingRMD = subtract(
      rmdAvailableBalance,
      totalPendingAndSystematicRMDAmount
    );
    const original = coalesce(totalRemainingRMD, 0);
    const RMDBalanceAmount = funds.reduce(remainingRMDBalance, original);
    return RMDBalanceAmount > 0 && !hasSystematicLE ? RMDBalanceAmount : 0;
  }
);

/**
 * Complex selector to check whether step2 has estimated text shown. if so this selector is used in step3 to show 'Est' text.
 * @param getSellFundsList {Selector}
 * @returns {Boolean}
 */
export const getIsTakeHomeEstimated = createSelector(
  getSellFundsList,
  (sellFundsList) => sellFundsList.some((fund) => fund.isTakeHomeEstimated)
);

/**
 * @param getSelectedSellFundsList {Selector} - gets sellLookup Object that contains shopping cart selections
 * @returns {Number} this returns the value of the shopping cart (sellTransaction)
 */
export const getSellTransactionTotal = createSelector(
  getSelectedSellFundsList,
  (selectedWithAmountSellFundsList) =>
    sumBy(selectedWithAmountSellFundsList, (fund) => fund.grossAmountDollars)
);

/**
 * Complex selector to extract the sum of totalDeductions of all selected funds.
 * @param getSelectedSellFundsList {Selector}
 * @returns {Number}
 */
export const getSellTransactionDeductionTotal = createSelector(
  getSelectedSellFundsList,
  (selectedWithAmountSellFundsList) =>
    sumBy(selectedWithAmountSellFundsList, (fund) => fund.totalDeductions)
);

/**
 * Complex selector to extract the sum of totalTakeHomeAmount of all selected funds.
 * @param getSelectedSellFundsList {Selector}
 * @returns {Number}
 */
export const getSellTakeHomeAmount = createSelector(
  getSelectedSellFundsList,
  (selectedWithAmountSellFundsList) =>
    sumBy(selectedWithAmountSellFundsList, (fund) => fund.netDollarAmount)
);

/**
 * @param getSelectedSellFundsList {Selector} - gets sellLookup Object that contains shopping cart selections
 * @returns {Number} this returns the number of funds in the shopping cart (sellTransaction)
 */
export const getSellSelectedFundsWithAmountCount = createSelector(
  getSelectedSellFundsList,
  (selectedWithAmountSellFundsList) => selectedWithAmountSellFundsList.length
);

export const getSellPendingTransactionsTotal = createSelector(
  getSellPendingTransactions,
  getSellFundsList,
  (sellPendingTransactions: PendingTransaction[], userFundList: UserFund[]) => {
    let pendingTransactionAmount = 0;
    userFundList.forEach((fund) => {
      sellPendingTransactions.forEach((sellPendingTransaction) => {
        const {
          transactionRecord: {
            sourcePositionKey: {
              agreementKey: { agreementID, agreementNumber },
            },
          },
          transactionRecords,
        } = sellPendingTransaction;
        const foundMatching =
          agreementID === fund.agreementID ||
          agreementNumber === fund.agreementNumber;
        if (foundMatching) {
          pendingTransactionAmount += sumBy(
            transactionRecords,
            (pendingTransaction: PendingTransaction) => {
              const { equityAsOf, sharePrice } = fund;
              // Get pending transaction unit
              const pendingTransactionUnit =
                pendingTransaction.unit.enumerationName;
              // Compute dollar amount based on share price if unit is in shares, return dollar amount otherwise
              switch (pendingTransactionUnit) {
                case SELECTED_UNIT_ENUM_OBJECT_MAP[UNIT.SHARES].enumerationName:
                  return round(pendingTransaction.shares * sharePrice, 2);
                case SELECTED_UNIT_ENUM_OBJECT_MAP[UNIT.ALL_SHARES]
                  .enumerationName:
                  return equityAsOf;
                default:
                  return pendingTransaction.amount;
              }
            }
          );
        }
      });
    });
    return pendingTransactionAmount;
  }
);

/**
 * Validated list of computed funds available to sell including sell-specific information and sell transaction
 * @param state
 * @returns {SellFund[]}
 */
export const getValidatedSellFundsList = createSelector(
  isCustomerWebViewEnabled,
  isSalesforceRep,
  getSellPendingTransactions,
  getSellPendingTransactionsTotal,
  getSellFundsList,
  getSellTransactionTotal,
  getTransactionLimit,
  (
    customerWebViewEnabled,
    isSalesforceRep,
    sellPendingTransactions,
    sellPendingTransactionsTotal,
    sellFundsList,
    sellTransactionTotal,
    transactionLimit
  ) =>
    sellFundsList
      .map((fund) => {
        const tempTransactionLimit = { ...transactionLimit };
        if (fund.productType === ProductTypes.MARYLAND529) {
          tempTransactionLimit.maximum =
            MD529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION;
        } else if (fund.productType === ProductTypes.ALASKA529) {
          tempTransactionLimit.maximum =
            AK529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION;
        } else if (fund.productType === ProductTypes.NATIONAL529) {
          tempTransactionLimit.maximum =
            NAT529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION;
        }
        setSellFundLimitMessage(
          customerWebViewEnabled,
          isSalesforceRep,
          fund,
          tempTransactionLimit
        );
        validateSellFund({
          fund,
          sellPendingTransactions,
          sellPendingTransactionsTotal,
          sellTransactionTotal,
          transactionLimit: tempTransactionLimit,
        });
        return fund;
      })
      .sort((a, b) => {
        if (a.displayClassification && b.displayClassification) {
          if (a.displayClassification > b.displayClassification) {
            return -1;
          } else if (a.displayClassification < b.displayClassification) {
            return 1;
          } else {
            return 0;
          }
        } else {
          return 0;
        }
      })
);

export const getHasAutoExchangesOrAutoSell = createSelector(
  [getAutoExchangeFunds, getRecurringRedemptionFunds, getRMDAgreements],
  (allAutoExchanges, allAutoSells, rmdAgreements) => {
    return (
      allAutoExchanges?.some((fund) => {
        return (
          rmdAgreements?.some(
            (rmdFund) =>
              fund?.sourceAgreementKey.agreementID === rmdFund.agreementID
          ) &&
          !rmdAgreements?.some(
            (rmdFund) => fund?.toFund.agreementID === rmdFund.agreementID
          )
        );
      }) ||
      allAutoSells?.some((fund) => {
        return rmdAgreements?.some(
          (rmdFund) =>
            fund?.sourceAgreementKey.agreementID === rmdFund.agreementID
        );
      })
    );
  }
);
export const getAutoSellEditFund = createSelector(
  [
    getValidatedSellFundsList,
    getRecurringRedemptionFunds,
    automaticEditActivityID,
  ],
  (
    sellFundList,
    autoSellFunds: SystematicSellFund[],
    automaticEditActivityID
  ) => {
    const index = findIndex(autoSellFunds, (fund) => {
      return automaticEditActivityID === fund.encryptedUniqueKey;
    });
    // todo: code smell! selectors should not have control over the window
    if (automaticEditActivityID && autoSellFunds.length > 0 && index < 0) {
      window.location.replace(`${config.url}${appendBasePath('/automatic')}`);
    }
    const { transferMonths, transferDays, ...editFund } =
      index >= 0 ? autoSellFunds[index] : ({} as SystematicSellFund);
    const [newFund] = sellFundList.filter(
      ({ agreementID }) => agreementID === editFund.agreementID
    );
    return [
      {
        ...editFund,
        ...newFund,
        transferMonths:
          transferMonths?.map<ItemSelection>((month) => ({
            label: getMonthName(month).toUpperCase(),
            value: month,
          })) ?? [],
        transferDays:
          transferDays?.map<ItemSelection>((day) => ({
            label: `${day}`,
            value: day,
          })) ?? [],
      } as SellFund,
    ];
  }
);

/**
 * Boolean selector to say if we have stopMail flag for all of the agreements
 * @param state
 * @returns {boolean}
 */
export const getStopMailFlag = createSelector(
  getUserFundList,
  (userFundList: SellFund[]) =>
    userFundList.every((fund) =>
      checkFoundFeaturesByCode(fund, SystemCode.STOP_MAIL_ID)
    )
);

export const getShowMoreBanks = (state) => state.existingBanks.show;

export const getDeliveryMethodTimelines = (state) =>
  !isNil(state.sellEstimationDates.estimationDates.deliveryTimelines)
    ? state.sellEstimationDates.estimationDates.deliveryTimelines
    : [];

/**
 * Simple selector to extract the address when getMultipleAddressFlag selector returns true
 * @param state
 * @returns {object}
 */
export const getAddress = (state) =>
  state.userFunds.funds?.length &&
  state.userFunds.funds[0].singleAddress !== null &&
  state.userFunds.funds[0].singleAddress;

/**
 * Boolean selector to check any of the fund has address change flag
 * @param state
 * @returns {boolean}
 */
export const getAddressChangeFlag = createSelector(
  getUserFundList,
  (userFundList: SellFund[]) =>
    !!userFundList.find((fund) => {
      return fund?.restrictedByAddressChange;
    })
);

/**
 * Boolean selector to say if we have address change flag for all funds
 * @param state
 * @returns {boolean}
 */
export const getEffectiveAddressChangedFlag = createSelector(
  getUserFundList,
  (userFundList: SellFund[]) =>
    userFundList.every((fund) => fund.restrictedByAddressChange)
);

/**
 * Boolean selector to say if we have addressChangedSameDay flag for all funds
 * @param state
 * @returns {boolean}
 */
export const getIsAddressChangedOnSameDay = createSelector(
  getEffectiveAddressChangedFlag,
  getUserFundList,
  (effectiveAddressChangedFlag, userFundList: SellFund[]) =>
    effectiveAddressChangedFlag &&
    userFundList.every((fund) => fund.addressChangedSameDay)
);

/**
 * Complex selector to extract the ' oldest effective date' when getEffectiveAddressChangedFlag selector returns true.
 * @param state
 * @returns {string}
 */
export const getEffectiveDate = createSelector(
  getUserFundList,
  getEffectiveAddressChangedFlag,
  (userFundList: SellFund[], effectiveAddressChangedFlag) =>
    !effectiveAddressChangedFlag
      ? null
      : userFundList
          .filter((fund) => !fund.addressChangedSameDay)
          .map((f) => f.address.effectiveDate)
          .sort((a, b) => Number(new Date(a)) - Number(new Date(b)))[0]
);

/**
 * Complex selector to extract the ' oldest effective date' when effectiveAddressChangedFlag is true and AddressChangedOnSameDay is false
 * else returns current date if AddressChangedOnSameDay is true for all funds.
 * @param state
 * @returns {string}
 */
export const getZeroStateEffectiveDate = createSelector(
  getEffectiveAddressChangedFlag,
  getIsAddressChangedOnSameDay,
  getEffectiveDate,
  (effectiveAddressChangedFlag, isAddressChangedOnSameDay, effectiveDate) => {
    if (effectiveAddressChangedFlag) {
      return isAddressChangedOnSameDay ? getCurrentDate() : effectiveDate;
    }
    return getCurrentDate();
  }
);

/**
 * Boolean selector to say if we have pending verification for all banks in step1 for sell
 * @param state
 * @returns {boolean}
 */
export const getIsAllBanksWithPreparingMicroDeposits = createSelector(
  getEligibleBanks,
  (eligibleBanks) =>
    eligibleBanks.length > 0 &&
    eligibleBanks.every(
      (bank) => bank.bankStatus === BANK_STATUS.PREPARING_MICRO_DEPOSITS
    )
);

export const getIsAllBanksWithProcessing = createSelector(
  getEligibleBanks,
  (eligibleBanks) =>
    eligibleBanks.length > 0 &&
    eligibleBanks.every((bank) => bank.bankStatus === BANK_STATUS.PROCESSING)
);

export const getStopTransfer = createSelector(
  getUserFundList,
  (userFundList: SellFund[]) =>
    userFundList.length >= 1 &&
    userFundList.every((fund) => {
      return checkFoundFeaturesByCode(fund, SystemCode.STOP_TRANSFER_ID);
    })
);

export const getIsBanksWithStatus = createSelector(
  getEligibleBanks,
  (eligibleBanks) =>
    eligibleBanks?.length > 0 &&
    eligibleBanks.every((bank) => bank.bankStatus !== null)
);

/**
 * Boolean selector to check below condition satisfies and check whether it is zero state or not.
 * @param state
 * @returns {boolean}
 */
export const getZeroState = createSelector(
  [
    getEffectiveAddressChangedFlag,
    getEligibleBanks,
    getStopMailFlag,
    getStopTransfer,
    getIsBanksWithStatus,
    getIsAddressChangedOnSameDay,
  ],
  (
    effectiveAddressChangedFlag,
    eligibleBanks,
    stopMail,
    stopTransfer,
    isBanksWithStatus,
    isAddressChangedOnSameDay
  ) =>
    (stopMail && !isAddressChangedOnSameDay) ||
    (stopMail && (isAddressChangedOnSameDay || effectiveAddressChangedFlag)) ||
    stopTransfer ||
    (effectiveAddressChangedFlag &&
      (eligibleBanks.length === 0 || isBanksWithStatus))
);

/**
 * Boolean selector to check when to display unavailable text for MailACheck delivery method .
 * @param state
 * @returns {boolean}
 */
export const getUnavailableText = createSelector(
  getEffectiveAddressChangedFlag,
  getMultipleAddressFlag,
  (effectiveAddressChangedFlag, multipleAddress) =>
    effectiveAddressChangedFlag && !multipleAddress
);

/**
 * Boolean selector to say if we have something selected for sell and everything selected is valid
 * @param state
 * @returns {boolean}
 */
export const getSellTransactionIsValid = createSelector(
  [
    getDeliveryMethod,
    getValidatedSellFundsList,
    getIsRMDDisclaimerAccepted,
    getHasAPP,
    getHasNotInheritedSEPOrSimpleIRA,
  ],
  (
    deliveryMethod,
    validatedSellFundsList,
    isRMDDisclaimerAccepted,
    hasAPP,
    hasNotInheritedSEPOrSimpleIRA
  ) => {
    const selectedFunds = validatedSellFundsList?.filter((fund) =>
      isSelectedWithAmount(fund)
    );
    return (
      ((selectedFunds.length > 0 &&
        selectedFunds.every(
          (fund) =>
            (isSelectedWithAmount(fund) && !isIRA(fund)) ||
            (isSelectedWithAmount(fund) &&
              isIRA(fund) &&
              fund.federalWithholdingPercent <= FederalTaxPercent.MAX)
        )) ||
        (deliveryMethod === DELIVERY_IN_KIND &&
          validatedSellFundsList?.some((fund) => fund.selected))) &&
      validatedSellFundsList
        .filter((fund) => fund.selected)
        .every((fund) => {
          return (
            isNil(fund.grossAmountError) || deliveryMethod === DELIVERY_IN_KIND
          );
        })
    );
  }
);

/**
 * Boolean selector to say if we have something selected for sell and everything selected is valid
 * @param state
 * @returns {boolean}
 */
export const getAutoSellTransactionIsValid = createSelector(
  [
    getValidatedSellFundsList,
    getIsRMDDisclaimerAccepted,
    getHasAPP,
    getHasNotInheritedSEPOrSimpleIRA,
    getSellTransactionLookup,
  ],
  (
    validatedSellFundsList,
    isRMDDisclaimerAccepted,
    hasAPP,
    hasNotInheritedSEPOrSimpleIRA,
    sellLookup
  ) => {
    const selectedFunds = validatedSellFundsList?.filter((fund) =>
      isSelectedWithAmount(fund)
    );
    return (
      selectedFunds.length > 0 &&
      validatedSellFundsList?.every(
        (fund) =>
          (isSelectedWithAmount(fund) &&
            fund.federalWithholdingPercent <= FederalTaxPercent.MAX &&
            isIRA(fund)) ||
          (!isIRA(fund) && isSelectedWithAmount(fund)) ||
          !isSelectedWithAmount(fund)
      ) &&
      validatedSellFundsList
        .filter((fund) => fund.selected)
        .every((fund) => {
          if (isNil(fund.grossAmountError)) {
            return (
              sellLookup[fund.agreementID]?.selectedMonths?.length > 0 &&
              sellLookup[fund.agreementID]?.selectedDays?.length > 0 &&
              fund.grossAmount > 0
            );
          } else {
            return false;
          }
        })
    );
  }
);

/**
 * Complex selector to extract the first 5 banks from eligibleBanks if the length of eligibleBanks is greater than 5.
 * @param state
 * @returns {array}
 */
export const getSellBankList = createSelector(
  getEligibleBanks,
  getShowMoreBanks,
  (eligibleBanks, showMoreBanks) =>
    eligibleBanks.length > 3 && !showMoreBanks
      ? eligibleBanks.filter((bank, i) => i < 3)
      : eligibleBanks
);

export const getIsCollapsedBankList = createSelector(
  getEligibleBanks,
  getShowMoreBanks,
  (eligibleBanks, showMoreBanks) => !showMoreBanks && eligibleBanks.length > 3
);

function createDeliveryTypeSelector(deliveryType) {
  return createSelector(
    getDeliveryMethodTimelines,
    (deliveryMethodTimelines) => {
      const directDepositDates = deliveryMethodTimelines.find(
        (timeLine) => timeLine.deliveryType.enumerationName === deliveryType
      );
      return directDepositDates && directDepositDates.estDates
        ? directDepositDates.estDates
        : { minimum: null, maximum: null };
    }
  );
}

/**
 * Complex selector to extract the estimateDates for DirectDeposit.
 * @param state
 * @returns {object}
 */
export const getDirectDepositTimelines = createDeliveryTypeSelector(
  'ElectronicTransferToBank'
);

/**
 * Complex selector to extract the estimateDates for ExpressDelivery.
 * @param state
 * @returns {object}
 */
export const getExpressDeliveryTimelines =
  createDeliveryTypeSelector('WireToBank');

/**
 * Complex selector to extract the estimateDates for MailACheck.
 * @param state
 * @returns {object}
 */
export const getMailACheckTimelines =
  createDeliveryTypeSelector('CheckToAddress');

/**
 * Complex selector to extract fund OwnerName from selected fund list only if all the selected funds has single and
 * same name.
 * @param state
 * @returns {string}
 */
export const getHasMultipleNames = createSelector(
  getSelectedSellFundsList,
  (selectedSellFundsList) => {
    const fundOwnerName =
      selectedSellFundsList.length > 0 &&
      selectedSellFundsList[0].formattedEntityNames;
    if (fundOwnerName.indexOf(',') === -1) {
      const fundOwnerNames = selectedSellFundsList.filter(
        (x) => x.formattedEntityNames === fundOwnerName
      );
      return fundOwnerNames.length === selectedSellFundsList.length
        ? fundOwnerName
        : '';
    }
  }
);

/**
 * Complex selector to sort the selected fundList with address and being primary sort by state and secondary sort by city.
 * @param state
 * @returns {Array}
 */
export const getSelectedFundsWithMultipleAddress = createSelector(
  getSelectedSellFundsList,
  (selectedSellFundsList) => {
    return sortBy(selectedSellFundsList, [
      (fund) => fund.address.state,
      (fund) => fund.address.city,
    ]);
  }
);

/**
 * Complex selector to group the selected funds with the same address. and Calculates sellTotalAmount, sellTotalDeduction,
 * sellTotalTakeHomeAmount for each group.
 * @param state
 * @returns {Object}
 */
export const getFundsByAddressLookup = createSelector(
  getSelectedFundsWithMultipleAddress,
  (selectedFundsWithMultipleAddress) => {
    const fundsByAddressLookup = groupBy(
      selectedFundsWithMultipleAddress,
      (fund) => fund.addressKey
    );

    Object.keys(fundsByAddressLookup).forEach((key) => {
      const selectedFunds = fundsByAddressLookup[key];
      fundsByAddressLookup[key] = {
        /** @ts-expect-error this type gets mutated. not cool. use a reduce here */
        funds: selectedFunds,
        groupData: {
          sellTotalAmount: sumBy(
            selectedFunds,
            (fund) => fund.grossAmountDollars
          ),
          sellTotalDeduction: sumBy(
            selectedFunds,
            (fund) => fund.totalDeductions
          ),
          sellTotalTakeHomeAmount: sumBy(
            selectedFunds,
            (fund) => fund.netDollarAmount
          ),
          isTakeHomeEstimated: selectedFunds.some(
            (fund) => fund.isTakeHomeEstimated
          ),
        },
      };
    });
    return fundsByAddressLookup;
  }
);

/**
 * Complex selector to group the selected funds with the same address for college distribute. and Calculates sellTotalAmount, sellTotalDeduction,
 * sellTotalTakeHomeAmount for each group.
 * Note: This duplicates much of the logic from getFundsByAddressLookup, but is kept separate to minimize changes that would impact other flows.
 * @param state
 * @returns {Object}
 */
export const getFundsByAddressLookupForCollegeDistribute = createSelector(
  getDeliveryMethod,
  getSelectedFundsWithMultipleAddress,
  getUserAddress,
  (deliveryMethod, selectedFundsWithMultipleAddress, userAddress) => {
    const fundsByAddressLookup = groupBy(
      selectedFundsWithMultipleAddress,
      function (fund: SellFund) {
        const {
          agreementParties,
          mailCheckBeneficiaryAddress: { street1: beneAddress } = {},
        } = fund;
        const isMailCheckWithBeneficiary =
          deliveryMethod === DELIVERY_MAIL_CHECK &&
          fund.payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.BENEFICIARY;
        const { businessPartyID: beneBusinessID } =
          (isMailCheckWithBeneficiary &&
            agreementParties?.find(
              ({ businessPartyID, primaryParty, roleCode }) =>
                primaryParty === 'N' &&
                roleCode === RoleCode.MINOR &&
                businessPartyID
            )) ||
          {};
        const beneAddressKey = isMailCheckWithBeneficiary && beneAddress;
        if (isMailCheckWithBeneficiary) {
          return `${beneAddressKey || fund.addressKey}-${
            fund.payee
          }-${beneBusinessID}`;
        }
        return `${fund.addressKey}-${fund.payee}`;
      }
    );
    Object.keys(fundsByAddressLookup).forEach((key) => {
      const selectedFunds = fundsByAddressLookup[key];
      // If Payee is Account Holder, address that is displayed needs to come from the details call, not the fund level address
      // This logic is done within the college distribute specific selector to avoid modifying the 'Sell' logic
      const selectedFundsWithUpdatedPayeeAddress = selectedFunds.map(
        (selectedFund) => {
          if (
            selectedFund.payee ===
            COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.ACCOUNT_HOLDER
          ) {
            return {
              ...selectedFund,
              address: userAddress,
            };
          }
          return selectedFund;
        }
      );
      fundsByAddressLookup[key] = {
        /** @ts-expect-error this type gets mutated. not cool. use a reduce here */
        funds: selectedFundsWithUpdatedPayeeAddress,
        groupData: {
          sellTotalAmount: sumBy(
            selectedFundsWithUpdatedPayeeAddress,
            (fund) => fund.grossAmountDollars
          ),
          sellTotalDeduction: sumBy(
            selectedFundsWithUpdatedPayeeAddress,
            (fund) => fund.totalDeductions
          ),
          sellTotalTakeHomeAmount: sumBy(
            selectedFundsWithUpdatedPayeeAddress,
            (fund) => fund.netDollarAmount
          ),
          isTakeHomeEstimated: selectedFundsWithUpdatedPayeeAddress.some(
            (fund) => fund.isTakeHomeEstimated
          ),
        },
      };
    });
    return fundsByAddressLookup;
  }
);

export const getIsStep2DistributionSelectionsValid = createSelector(
  getIsMailACheck,
  getSelectedSellFundsList,
  getSellTransactionIsValid,
  getIsMailACheckToSchool,
  getStudentDetails,
  (
    isMailACheck,
    selectedSellFundsList,
    sellTransactionIsValid,
    isMailACheckToSchool,
    studentDetail
  ) => {
    return (
      sellTransactionIsValid &&
      selectedSellFundsList.every(
        (fund) =>
          (isMailACheckToSchool && hasStudentID(studentDetail)) ||
          (fund.payee &&
            (isMailACheck || fund.distributionPayeeDisclaimerAccepted))
      )
    );
  }
);

export const getSelectedSellFundBene = createSelector(
  getSellFundsList,
  (sellFunds) => {
    const selectedFunds = sellFunds.filter((f) => f.selected);
    return selectedFunds.length > 0 && selectedFunds[0].formattedEntityNames;
  }
);

/**
 /**
 * Complex selector to return if a delivery method is classified as an electonic funds transfer (involves a bank)
 * @param state
 * @returns {boolean}
 */
export const getIsEFT = (state) => getIsWire(state) || getIsACH(state);

/**
 * Boolean selector to say whether Next Button is step1 should be disabled or not
 */
export const getIsStep1NextButtonDisabled = createSelector(
  [
    getIsMailACheck,
    getSelectedBank,
    getUnavailableText,
    getZeroState,
    getIsBanksWithStatus,
    getIsEFT,
    getDeliveryMethod,
    getIsMailACheckToSchool,
    getSelectedSchool,
  ],
  (
    isMailACheck,
    selectedBank,
    unavailableText,
    zeroState,
    isBanksWithStatus,
    isEFT,
    deliveryMethod,
    isMailACheckToSchool,
    selectedSchool
  ) => {
    return (
      ((isNil(selectedBank) || isBanksWithStatus) && isEFT) ||
      (isNil(selectedSchool) && isMailACheckToSchool) ||
      (isMailACheck && unavailableText) ||
      zeroState ||
      isEmpty(deliveryMethod)
    );
  }
);

export const getIsZeroDollarBalanceFund = createSelector(
  getUserFundList,
  (userFundList: SellFund[]) => {
    return userFundList.every((fund) => !sellFundFilter(fund));
  }
);

/**
 * getBankDetailsInArray that returns bank Details
 * @param {fund} fund
 * @returns an array with bank details if it has selected bank else returns null.
 * returns null as the backend-service for the print needed in this way.
 */
export const getBankDetailsInArray = createSelector(
  getSelectedBank,
  (selectedBank) => {
    if (selectedBank) {
      const { name, bankAccountType, accountNumber, registrationLines } =
        selectedBank;
      return [
        name,
        `${bankAccountType} ${getLastFour(accountNumber)}`,
        registrationLines?.join(', '),
      ];
    }
    return null;
  }
);

export function timeLine(prefix) {
  return `${formatUTCDate(prefix.minimum, 'MM/DD/YY')} - ${formatUTCDate(
    prefix.maximum,
    'MM/DD/YY'
  )}`;
}

/**
 * getAddressDetailsInArray that returns address and entityNames
 * @param {fund} fund
 * @returns an array with address and entityNames if it has fund else returns null.
 * returns null as the backend-service for the print needed in this way.
 */
export function getAddressDetailsInArray(fund) {
  const { address, formattedEntityNames } = fund;
  if (isNil(address) && isNil(formattedEntityNames)) {
    return null;
  }
  const { street1, city, state, zip } = address;
  return [formattedEntityNames, street1, city, state, zip];
}

/**
 * Used to determine whether to show review steps as separate blocks or a single block in college distribute review
 * @returns {Boolean} indicates if there are multiple addresses or multiple payees to show in the college distribute review step
 */
export const getCollegeDistributeMultiplePayeeOrAddress = createSelector(
  getMultipleAddressFlag,
  getSelectedFundsWithMultipleAddress,
  (multipleAddressFlag, selectedFundsWithMultipleAddress) => {
    const hasMultiplePayees =
      selectedFundsWithMultipleAddress.some(
        ({ payee }) => payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.ACCOUNT_HOLDER
      ) &&
      selectedFundsWithMultipleAddress.some(
        ({ payee }) => payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.BENEFICIARY
      );
    const hasMultipleBeneAddress =
      selectedFundsWithMultipleAddress.filter(
        (fund) =>
          fund.mailCheckBeneficiaryAddress &&
          fund.payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.BENEFICIARY
      ).length > 1;
    return multipleAddressFlag || hasMultiplePayees || hasMultipleBeneAddress;
  }
);

/**
 * Used to get the beneficiary address when distribution is via Check and multiple address flag is not true
 */
export const getCollegeDistributeAddress = createSelector(
  getUserAddress,
  getSelectedSellFundsList,
  (userAddress, selectedSellFundsList) => {
    if (
      selectedSellFundsList.length > 0 &&
      selectedSellFundsList[0].payee ===
        COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.BENEFICIARY &&
      selectedSellFundsList[0].mailCheckBeneficiaryAddress
    ) {
      return selectedSellFundsList[0].mailCheckBeneficiaryAddress;
    }
    return userAddress;
  }
);

export const get529MaximumAmount = createSelector(
  [getHasMD529Only, getHasAK529Only, getHasNAT529Only],
  (hasMd529Only, hasAk529Only, hasNat529Only) => {
    if (hasMd529Only) {
      return currency(MD529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION);
    }
    if (hasAk529Only) {
      return currency(AK529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION);
    }
    if (hasNat529Only) {
      return currency(NAT529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION);
    }
    return currency(COLLEGE_DISTRIBUTE_MAX_REDEMPTION);
  }
);
